import { HotelCitiesMainContentPropertiesType } from "@website/accommodation-ui/hotel-cities-main-content";
import { useFetcher, useFetchers } from "@website/fetcher/use-fetcher";
import { useLoginStatus } from "@website/hooks/auth-manager";
import { Button } from "@website/ui/button";
import { useSnackbar } from "@website/ui/snackbar";
import { getChannel } from "@website/utils/channel/get-channel";
import { getOrigin } from "@website/utils/channel/get-origin";
import { isChannel } from "@website/utils/channel/helpers";
import { isInvalidCheckin, today, tomorrow } from "@website/utils/date-format";
import { isFilled } from "@website/utils/is-filled";
import { replaceSnappTripBasedOnChannel } from "@website/utils/title-replace";
import {
  MapFilterProperties,
  useCityFilterState,
  useFilters
} from "@website/widget/filter";
import { useSnappPro } from "@website/widget/header/hooks/use-snapp-pro";
import { TelesalesAppName } from "@website/widget/telesales";
import first from "lodash-es/first";
import get from "lodash-es/get";
import isEmpty from "lodash-es/isEmpty";
import map from "lodash-es/map";
import omit from "lodash-es/omit";
import orderBy from "lodash-es/orderBy";
import pick from "lodash-es/pick";
import toString from "lodash-es/toString";
import uniq from "lodash-es/uniq";
import { useRouter } from "next/router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHotelCitiesFilter } from "./filter.hook";
import {
  fetchCityFilters,
  fetchHotelRateData,
  fetchHotelReviewData,
  fetchHotelsData,
  fetchInstalmentWallet,
  fetchLastSecondHotels,
  fetchVillaBanner
} from "./index.server";
import { handleEventMapSearchClicked } from "./index.tracking";
import { CityPagePropertiesType, reasonsSchema } from "./index.types";
import { useHotelSearch } from "./search.hook";

export const getBannersByType = (data, type: string) =>
  data?.banners
    ?.find?.((item) => item?.type === type)
    ?.data?.sort?.((a, b) => a?.order - b?.order) ||
  null ||
  [];

export const useCityPage = (properties: CityPagePropertiesType) => {
  const {
    locationData,
    cityData,
    hotelSuggestionsData,
    hotelsData,
    hasError,
    lastSecondHotels,
    inlineBanners,
    staticBanners,
    smallBanners
  } = properties;
  const router = useRouter();
  const [pages, setPages] = useState<Array<number>>([1]);
  const [updatedModal, setUpdatedModal] = useState<boolean>(false);
  const timerReference = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchProperties = useHotelSearch({ cityData });
  const { hasSnappPro } = useSnappPro();
  const selectedHotelId = router.query?.selectedHotelId;

  const { enqueue, dequeue } = useSnackbar();

  const { onOpenFilters } = useCityFilterState();

  const hasPromotion = Boolean(router.query["promotion"]);
  const { isLoggedIn } = useLoginStatus();
  const {
    data: instalmentWallet,
    isLoading: isInstallmentWalletLoading,
    isFetching: isInstallmentWalletFetching
  } = useFetcher({
    enabled: isLoggedIn,
    retry: false,
    queryKey: ["instalmentWallet"],
    queryFn: () => fetchInstalmentWallet()
  });

  const freshHotelsData = useFetchers({
    queries: pages.map((page) => ({
      enabled: !isInvalidCheckin(router.query.date_from, router.query.date_to),
      queryKey: [
        "hotelsData",
        cityData?.id,
        omit(router.query, ["modal", "selectedHotelId", "popover", "item"]),
        page
      ],
      queryFn: ({ queryKey }: { queryKey: Array<unknown> }) =>
        get(queryKey[2], "promotion")
          ? fetchLastSecondHotels({
              ...(queryKey[2] as object),
              page: queryKey[3]
            })
          : fetchHotelsData(queryKey[1], {
              ...(queryKey[2] as object),
              page: queryKey[3]
            })
    }))
  });
  const { data: cityFilters } = useFetcher({
    enabled: freshHotelsData?.[0]?.isFetched,
    queryKey: [
      "cityFilters",
      cityData?.id,
      router.query?.["date_from"],
      router.query?.["date_to"],
      omit(router.query, "facilities")
    ],
    queryFn: () => fetchCityFilters(cityData?.id, router?.query)
  });

  const freshLastSecondHotelsData = useFetcher({
    refetchOnMount: true,
    queryKey: ["lastSecondHotelsData", router.query],
    queryFn: ({ queryKey }: { queryKey: Array<unknown> }) =>
      fetchLastSecondHotels(queryKey[1] as object)
  });

  const updateResultSearchHandler = useCallback(
    () =>
      Promise.all(freshHotelsData.map(({ refetch }) => refetch())).finally(() =>
        setUpdatedModal(false)
      ),
    [freshHotelsData]
  );

  const coordinate = useMemo(() => {
    const { latitude, longitude } = locationData?.location || {};
    return latitude && longitude ? `${latitude},${longitude},11,33` : null;
  }, [locationData]);

  const goToMapFilter = useCallback(() => {
    handleEventMapSearchClicked({
      city: locationData?.title,
      city_id: locationData?.id,
      date_from: toString(router.query?.["date_from"]),
      date_to: toString(router.query?.["date_to"])
    });

    router.push({
      pathname: "/map",
      query: {
        ...(coordinate ? { coordinate } : {}),
        ...omit(router.query, ["cityTitle"])
      }
    });
  }, [locationData?.title, locationData?.id, router, coordinate]);
  const onSortChange = useCallback(
    (value?: string) =>
      router.push(
        {
          pathname: router.pathname,
          query: { ...router.query, order_by: value }
        },
        undefined,
        { shallow: true, scroll: false }
      ),
    [router]
  );

  const mapFilterProperties: MapFilterProperties = {
    label: "نمایش روی نقشه",
    onClick: goToMapFilter
  };
  useEffect(() => {
    timerReference.current = setTimeout(() => setUpdatedModal(true), 900_000);
    return () => {
      timerReference.current && clearTimeout(timerReference.current);
    };
  }, []);

  useEffect(() => {
    if (
      isInvalidCheckin(router.query.date_from, router.query.date_to) ||
      (router.query["promotion"] === "last-minute" &&
        (router.query["date_from"] !== today() ||
          router.query["date_to"] !== tomorrow()))
    ) {
      // TODO: handle it in ssr
      router.replace(
        {
          pathname: "/[cityTitle]",
          query: omit(router.query, ["date_from", "date_to"])
        },
        undefined,
        { shallow: true, scroll: false }
      );
    }
  }, [router.query.date_from, router.query.date_to, router.query.promotion]);

  useEffect(() => {
    if (
      ["date_from", "date_to", "adults"].every(
        (key) => !isFilled(router.query[key])
      )
    ) {
      const buttonProperties = {
        onClick: () => {
          searchProperties.setIsSearchPanelOpen.bind(null, true)();
          setTimeout(() => {
            dequeue(snackBarId);
          }, 100);
        },
        color: "secondary" as const,
        variant: "text" as const
      };
      const snackBarId = enqueue(
        "برای مشاهده قیمت دقیق اقامتگاه‌ها٬ تاریخ را وارد کنید.",
        "default",
        "one-line",
        <Button {...buttonProperties}>تکمیل اطلاعات</Button>
      );
    }
  }, [
    router.query.date_from,
    router.query.date_to,
    router.query.adults,
    searchProperties.setIsSearchPanelOpen,
    enqueue,
    dequeue
  ]);

  const loading = useMemo(
    () =>
      !freshHotelsData?.length ||
      freshHotelsData?.some(
        ({ isFetching, status }) => isFetching || status === "pending"
      ),
    [freshHotelsData]
  );

  const filteredHotels = useMemo(() => {
    if (isEmpty(omit(router.query, ["cityTitle"])) && loading) {
      return hotelsData;
    }
    return (
      freshHotelsData
        ?.flatMap(({ data }) => data)
        .filter(Boolean)
        .map((hotel) => ({
          ...hotel,
          hasSnappProDiscount:
            hasSnappPro &&
            hotel.pricing?.discountPercentage > 0 &&
            hotel.pricing?.hasSnappProDiscount
        })) || []
    );
  }, [freshHotelsData, hotelsData, loading, router.query, hasSnappPro]);

  const totalHotelsCount = useMemo(
    () => first(filteredHotels)?.total,
    [filteredHotels]
  );

  const citiesFilterProperties = useHotelCitiesFilter(
    cityFilters?.categories,
    cityFilters?.priceRange,
    cityFilters?.facilities || [],
    cityFilters?.stars || [],
    cityFilters?.accommodations || [],
    cityFilters?.rates || [],
    totalHotelsCount
  );
  const { selectedFiltersCount } = useFilters({
    schema: citiesFilterProperties.filtersSchema
  });

  useEffect(() => {
    if (getChannel() === "walkin") {
      router.replace({
        pathname: router.pathname,
        query: {
          ...router.query,
          channel: "website"
        }
      });
    }
  }, [router]);

  const filteredPages = useMemo(
    () => orderBy(uniq(map(filteredHotels, "page"))),
    [filteredHotels]
  );

  const { data: hotelRateData } = useFetcher({
    refetchOnMount: true,
    enabled: Boolean(selectedHotelId),
    queryKey: ["hotelRateData", selectedHotelId],
    queryFn: ({ queryKey }: { queryKey: Array<unknown> }) =>
      fetchHotelRateData(queryKey[1])
  });

  const { data: hotelReviewData } = useFetcher({
    refetchOnMount: true,
    enabled: Boolean(selectedHotelId),
    queryKey: ["hotelReviewData", selectedHotelId],
    queryFn: ({ queryKey }: { queryKey: Array<unknown> }) =>
      fetchHotelReviewData(
        queryKey[1],
        1,
        "hotel-domestic",
        {} as Record<string, string>
      )
  });

  const { data: villaBannerData } = useFetcher({
    refetchOnMount: true,
    enabled: Boolean(cityData?.id),
    queryKey: [
      "villaBannerData",
      cityData?.id,
      router?.query?.["date_from"],
      router?.query?.["date_to"]
    ],
    queryFn: ({ queryKey }: { queryKey: Array<unknown> }) =>
      fetchVillaBanner(
        String(queryKey[1]),
        String(queryKey[2] || today()),
        String(queryKey[3] || tomorrow())
      )
  });

  const onMapClick = useCallback(
    () =>
      router?.push(
        {
          pathname: "/map",
          query: {
            ...omit(router?.query, ["cityTitle", "modal"]),
            coordinate
          }
        },
        undefined,
        { shallow: true, scroll: false }
      ),
    [coordinate, router]
  );

  return {
    searchProperties,
    citiesFilterProperties: {
      ...citiesFilterProperties,
      mapFilterProps: coordinate && mapFilterProperties,
      loading
    },
    modalProps: {
      isOpen: updatedModal,
      onUpdateResult: updateResultSearchHandler
    },
    hotelListHeaderProps: {
      loading,
      chipsFiltersSchema: citiesFilterProperties.chipsFiltersSchema,
      clearAllFilters: citiesFilterProperties.clearAllFilters,
      setFilterOpen: onOpenFilters,
      city: cityData?.title,
      totalItem: totalHotelsCount,
      selectedFiltersCount,
      // children: coordinate ? (
      //   <div className="relative xl:hidden">
      //     <Button
      //       StartIcon={<MaterialIcon name="map" variant="outlined" />}
      //       children={"نقشه"}
      //       color="neutral"
      //       onClick={goToMapFilter}
      //       size="small"
      //       variant="round-outline"
      //     />
      //     <Badge className="absolute top-[-7px]" content={"جدید"} />
      //   </div>
      // ) : null,
      sortProperties: {
        onChange: onSortChange,
        initialValue: toString(router.query.order_by),
        list: [
          {
            label: replaceSnappTripBasedOnChannel("پیشنهاد اسنپ تریپ"),
            value: "selling"
          },
          {
            label: "براساس امتیاز کاربران",
            value: "rating"
          },
          {
            label: "ارزانترین",
            value: "min_sum_local_price_off"
          },
          {
            label: "گرانترین",
            value: "max_sum_local_price_off"
          },
          {
            label: "بیشترین تخفیف",
            value: "discount"
          }
        ]
      }
    },
    hotelPaginationProps: {
      pages: filteredPages,
      setPages,
      loading,
      totalCount: totalHotelsCount
    },
    hotelCitiesMainContentProps: {
      totalItem: totalHotelsCount,
      cityTitle: cityData?.title,
      clearAllFilters: citiesFilterProperties.clearAllFilters,
      hasError: hasError,
      hasFilters: selectedFiltersCount !== 0,
      selectedHotelId: Number(selectedHotelId),
      hotelList: filteredHotels,
      reviewData: {
        rate: hotelRateData,
        comments: hotelReviewData?.rateReviews
          ?.map((comment) => comment)
          ?.splice(0, 5)
      } as unknown as HotelCitiesMainContentPropertiesType["reviewData"],
      hotelsSuggested: hasPromotion ? [] : hotelSuggestionsData,
      loading,
      setIsSearchPanelOpen: searchProperties.setIsSearchPanelOpen,
      snappPayData: {
        isEligible: instalmentWallet?.isEligible,
        showDownPayment: instalmentWallet?.showDownPayment,
        markup: instalmentWallet?.markup,
        isLoading: isInstallmentWalletLoading || isInstallmentWalletFetching
      },
      bannerSlideList: getBannersByType(staticBanners, "static"),
      inlineBannerList: getBannersByType(inlineBanners, "static"),
      ...(villaBannerData?.show
        ? {
            accommodationData: {
              cityTitle: villaBannerData?.title,
              onClick: () =>
                (location.href = `/villa/city/${villaBannerData?.citySlug}?date_from=${router?.query["date_from"]}&date_to=${router?.query["date_to"]}&utm_source=cityPage&utm_content=villa`)
            }
          }
        : {}),
      lastSecondHotelsList: hasPromotion
        ? []
        : freshLastSecondHotelsData?.isLoading
          ? lastSecondHotels
          : freshLastSecondHotelsData.data,
      lastSecondHotelsLoading: freshLastSecondHotelsData.isFetching
    },
    hotelBreadcrumbsProperties: {
      color: "default" as const,
      variant: "default" as const,
      list: [
        {
          title: "رزرو هتل",
          href: getOrigin()
        },
        {
          title: `رزرو هتل های ${cityData?.title}`,
          href: {
            pathname: "/[cityTitle]",
            query: pick(router.query, ["cityTitle"])
          }
        }
      ]
    },
    telesaleProperties: {
      description:
        " بعد از ثبت درخواست، کارشناسان ما در اسرع وقت با شما تماس خواهند گرفت و شما را برای رزرو هتل داخلی راهنمایی خواهند کرد.",
      reasons: reasonsSchema,
      requestData: {
        app: "hotel" as TelesalesAppName,
        checkin: toString(router?.query?.["date_from"] || today()),
        checkout: toString(router?.query?.["date_to"] || tomorrow()),
        city_from: "",
        city_to: toString(router?.query?.["cityTitle"]),
        funnel: "telesales_box"
      },
      storageKey: "hotel_submit-tele-sale-reserve"
    },
    bannersProperties: {
      bannerSlideList: getBannersByType(smallBanners, "small")
    },
    onMapClick,
    isAdChannel: isChannel(["snapp-ir", "ads"])
  };
};
